/**
 * Node pools in the console (JSS#1104, ADR-JSS-0011): the keys as
 * `@jeket-cloud/site-contract` defines them (the SPA does not bundle the
 * contract package, so they are restated; `nodePools.test.ts` fails if they
 * drift from contract.json), and the words the Node pools page uses.
 */
import type { NodePoolLayout, NodePoolsErrorBody, NodePoolStage, NodePoolsView } from '@jeket-cloud/types';
import { ApiError } from './apiError';

/** Node label naming a node's pool. */
export const NODE_POOL_LABEL = 'jeket.com/node-pool';

/** Prefix of the per-class accept label: `accepts.jeket.com/<class>=true`. */
export const ACCEPTS_LABEL_PREFIX = 'accepts.jeket.com/';

/** The taint on a dedicated pool's nodes, which only the site's node pools set. */
export const NODE_POOL_TAINT_KEY = 'jeket.com/node-pool';

export function isNodePoolTaintKey(key: string): boolean {
  return key === NODE_POOL_TAINT_KEY;
}

/** A node's pool from its labels, or null. */
export function nodePool(labels: Record<string, string> | undefined): string | null {
  return labels?.[NODE_POOL_LABEL] ?? null;
}

/** The classes a node's pool accepts, from its labels. */
export function nodeAccepts(labels: Record<string, string> | undefined): string[] {
  return Object.entries(labels ?? {})
    .filter(([k, v]) => k.startsWith(ACCEPTS_LABEL_PREFIX) && v === 'true')
    .map(([k]) => k.slice(ACCEPTS_LABEL_PREFIX.length));
}

export const CLASS_DESCRIPTIONS: Record<string, string> = {
  control: 'Management plane: orchestrator, operators, NMOS, databases',
  observability: 'Monitoring, metrics and logs',
  build: 'CI runners and builds',
  media: 'Media functions',
  ai: 'AI inference',
  volumetric: 'Volumetric capture',
  strobe: 'Strobe monitors',
};

export const STAGE_TEXT: Record<NodePoolStage, { label: string; description: string }> = {
  none: { label: 'Off', description: 'Pools are not on the cluster. Nothing schedules differently.' },
  labelled: { label: 'Labelled', description: 'Nodes carry their pool and the classes it accepts. Nothing schedules differently yet.' },
  enforced: { label: 'Enforced', description: 'New pods of a class only schedule on nodes whose pool accepts it. Running pods are untouched.' },
  dedicated: { label: 'Dedicated', description: "Dedicated pools' nodes are tainted: only pods that tolerate it schedule there. Nothing is evicted." },
};

export const NODE_POOL_STAGES: readonly NodePoolStage[] = ['none', 'labelled', 'enforced', 'dedicated'];

export const CONFORMANCE_REASON_TEXT: Record<string, string> = {
  class_not_accepted: "Its node's pool does not accept its class",
  unfenced_on_dedicated: 'Declares no class and sits on a dedicated node',
  unschedulable: 'Cannot schedule: no node accepts its class',
};

/** The layout a view stores, as a draft to edit. */
export function layoutOf(view: Pick<NodePoolsView, 'pools' | 'assignments'>): NodePoolLayout {
  return {
    pools: view.pools.map((p) => ({ ...p, accepts: [...p.accepts] })),
    assignments: { ...view.assignments },
  };
}

/** A node-pool API error's body (`error` prose, `code`, problems…), or a plain message. */
export function nodePoolsError(err: unknown): NodePoolsErrorBody {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const b = err.body as Partial<NodePoolsErrorBody>;
    return { ...b, error: typeof b.error === 'string' ? b.error : `Request failed (${err.status})` };
  }
  return { error: err instanceof Error ? err.message : String(err) };
}
